// First-visit failure guards for the app shell, installed by main.ts at boot:
//
//  - webgl2Available / showWebGLFallback / haltBoot: renderer-creation guard.
//    On a browser/device without working WebGL2 the WebGLRenderer constructor
//    throws and the user would get a silent dark-blue blank screen. The
//    fallback overlay is built with raw DOM on purpose — it runs at the very
//    top of main.ts's eval-time code, before any app UI machinery is
//    installed, and must not depend on anything that could itself fail.
//  - installContextLossRecovery: webglcontextlost/restored handling on the
//    renderer's canvas.
//  - installSmallScreenNotice: dismissible coarse-pointer + small-viewport
//    warning; dismissal persists via ui/settings-store.

import { showAlert } from "../ui/modal";
import { loadSettings, saveSetting } from "../ui/settings-store";

export function webgl2Available(): boolean {
  try {
    return !!document.createElement("canvas").getContext("webgl2");
  } catch {
    return false;
  }
}

// Shared card look — matches ui/modal.ts's .dt-modal styling, inlined so this
// module stays dependency-free for the pre-boot fallback path.
const CARD_CSS =
  "width:380px;max-width:calc(100vw - 32px);padding:24px;border-radius:16px;" +
  "display:flex;flex-direction:column;gap:12px;" +
  "background:linear-gradient(180deg, rgba(36,39,50,0.98), rgba(26,28,37,0.98));" +
  "border:1px solid rgba(255,255,255,0.12);" +
  "box-shadow:0 18px 50px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06) inset;";
const OVERLAY_FONT = "font:13px/1.5 system-ui,-apple-system,sans-serif;color:#eceff6;";

function makeCard(title: string, message: string): { root: HTMLDivElement; card: HTMLDivElement } {
  const root = document.createElement("div");
  const card = document.createElement("div");
  card.style.cssText = CARD_CSS;
  const h = document.createElement("h3");
  h.style.cssText = "margin:0;font-size:15px;font-weight:650;letter-spacing:0.01em;";
  h.textContent = title;
  const p = document.createElement("p");
  p.style.cssText = "margin:0;color:#b9bfd0;";
  p.textContent = message;
  card.append(h, p);
  root.appendChild(card);
  return { root, card };
}

// Full-screen static notice: no dismiss, nothing behind it works anyway.
export function showWebGLFallback(): void {
  const { root } = makeCard(
    "TinyDraft can't start on this browser",
    "TinyDraft needs a browser with WebGL2 — a recent Chrome, Edge, Firefox, " +
      "or Safari on desktop, or a VR headset browser.",
  );
  root.style.cssText =
    "position:fixed;inset:0;z-index:100;display:flex;align-items:center;" +
    "justify-content:center;background:#1a1a2e;" +
    OVERLAY_FONT;
  document.body.appendChild(root);
}

// Stop the rest of main.ts's eval-time code by throwing — main.ts is the entry
// module, so rejecting its evaluation halts boot with no follow-on exceptions
// from later top-level code running against a missing renderer. The window
// "error" listener cancels the browser's default uncaught-error console entry
// for this one deliberate sentinel (the failure is already handled: the
// fallback overlay is showing); real errors still report normally.
export function haltBoot(reason: string): never {
  const sentinel = new Error(`TinyDraft boot halted: ${reason}`);
  window.addEventListener("error", (e) => {
    if (e.error === sentinel) e.preventDefault();
  });
  throw sentinel;
}

// If the GPU process resets (driver crash, OS sleep, tab backgrounding on some
// mobile GPUs), preventDefault on `webglcontextlost` tells the browser we want
// the context back; on restore the idle-gated render loop must be woken or the
// canvas keeps its stale (now blank) frame. If restore doesn't arrive within a
// few seconds, suggest a reload.
const CONTEXT_RESTORE_GRACE_MS = 4000;

export function installContextLossRecovery(canvas: HTMLCanvasElement, wake: () => void): void {
  let lostTimer: number | null = null;
  canvas.addEventListener("webglcontextlost", (e) => {
    e.preventDefault();
    if (lostTimer !== null) return;
    lostTimer = window.setTimeout(() => {
      lostTimer = null;
      void showAlert(
        "The graphics context was lost and hasn't come back. " +
          "Reloading the page should fix this.",
        { title: "Graphics context lost" },
      );
    }, CONTEXT_RESTORE_GRACE_MS);
  });
  canvas.addEventListener("webglcontextrestored", () => {
    if (lostTimer !== null) {
      clearTimeout(lostTimer);
      lostTimer = null;
    }
    wake();
  });
}

// The editor has no touch camera controls and a fixed desktop layout — on
// phones it's broken, not degraded. Coarse primary pointer + small viewport →
// one dismissible notice. Deliberately NOT a hard wall: VR-headset browsers
// can report coarse pointers, and dismissal is remembered.
const SMALL_SCREEN_MAX_W = 900;

export function installSmallScreenNotice(): void {
  if (loadSettings().smallScreenNoticeDismissed) return;
  if (!window.matchMedia("(pointer: coarse)").matches) return;
  if (window.innerWidth >= SMALL_SCREEN_MAX_W) return;

  const { root, card } = makeCard(
    "Best on a bigger screen",
    "TinyDraft is built for desktop browsers and VR headsets — things may " +
      "not work well on this device.",
  );
  root.style.cssText =
    "position:fixed;inset:0;z-index:60;display:flex;align-items:center;" +
    "justify-content:center;background:rgba(8,10,16,0.55);" +
    "backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);" +
    OVERLAY_FONT;
  const row = document.createElement("div");
  row.style.cssText = "display:flex;justify-content:flex-end;margin-top:4px;";
  const btn = document.createElement("button");
  btn.style.cssText =
    "font:inherit;font-weight:600;cursor:pointer;border-radius:8px;" +
    "padding:7px 14px;background:#6fa0ff;color:#11141c;border:1px solid #6fa0ff;";
  btn.textContent = "Got it";
  row.appendChild(btn);
  card.appendChild(row);

  const dismiss = (): void => {
    saveSetting("smallScreenNoticeDismissed", true);
    root.remove();
  };
  btn.addEventListener("click", dismiss);
  root.addEventListener("pointerdown", (e) => {
    if (e.target === root) dismiss(); // tap the backdrop to dismiss
  });
  document.body.appendChild(root);
}
